/**
 * A stopwatch that only runs while the user could actually see the thing.
 *
 * It exists because the number it feeds — "time spent engaged with the widget" —
 * goes on an operator's dashboard as evidence that the widget drives attention.
 * Wall-clock would count a phone in a pocket and a tab left open overnight, which
 * is not evidence of anything, and an inflated engagement figure is the kind of
 * claim this codebase has refused everywhere else (`combined_odds_suppressed`,
 * `influenced` vs `attributed`).
 *
 * So the clock pauses on `visibilitychange` and resumes when the page comes back.
 * A card left open on a hidden tab accrues nothing.
 *
 * Not a general-purpose timer: `stop()` is terminal and returns the total, because
 * every caller measures one opening and then reports it.
 */
export class EngagedClock {
  private accrued = 0;
  /**
   * ms epoch the current visible run started; `null` when paused or stopped.
   *
   * NOT 0 as the sentinel — a legitimate `start(0)` would then read as "paused",
   * which is precisely the bug the first cut of this class shipped with and which
   * its tests caught.
   */
  private since: number | null = null;
  private running = false;
  private onVis: (() => void) | null = null;

  /** `isHidden` is injected so this is testable without touching `document`. */
  constructor(
    private isHidden: () => boolean =
      () => typeof document !== "undefined" && document.hidden === true,
  ) {}

  start(now = Date.now()): void {
    if (this.running) return;
    this.running = true;
    this.accrued = 0;
    this.since = this.isHidden() ? null : now;
    try {
      this.onVis = () => this.sync();
      document.addEventListener("visibilitychange", this.onVis, false);
    } catch {
      // No document (tests, SSR): the clock still measures, it just cannot pause.
      this.onVis = null;
    }
  }

  /** Fold the elapsed visible run into the total and re-arm per current state. */
  private sync(now = Date.now()): void {
    if (!this.running) return;
    if (this.isHidden()) {
      if (this.since != null) {
        // A backwards clock (device sleep, NTP step) can hand us a negative delta.
        this.accrued += Math.max(0, now - this.since);
        this.since = null;
      }
    } else if (this.since == null) {
      this.since = now;
    }
  }

  /** Total VISIBLE ms so far, without stopping. */
  elapsed(now = Date.now()): number {
    if (!this.running) return this.accrued;
    return this.accrued + (this.since != null ? Math.max(0, now - this.since) : 0);
  }

  /** Terminal. Returns the total visible ms and detaches the listener. */
  stop(now = Date.now()): number {
    const total = this.elapsed(now);
    this.running = false;
    this.since = null;
    this.accrued = total;
    if (this.onVis) {
      try {
        document.removeEventListener("visibilitychange", this.onVis, false);
      } catch {
        /* document gone */
      }
      this.onVis = null;
    }
    return Math.round(total);
  }
}
